import React, { forwardRef, useMemo } from 'react';
import { useMutation, useSubscription } from '@apollo/client';
import { listAssignedStaffMembersSubscription, sendNewScheduleNotification } from '@/graph/staff';
import { format } from 'date-fns';
import StaffMember from '@/types/StaffMember';
import { useScope } from '@/hooks/useScope';
import { Destination } from '@/types/NotificationTemplate';
import { MenuItem, MenuItemProps } from '@material-ui/core';
import { TooltipWrapper } from '../SendNotificationDropdown';
import SendNotificationDialog, { Contact } from '../anesthesiologist/SendNotificationDialog';

type StaffMemberContact = {
  staffId: number;
  staffHospitalId: number;
  phoneNumber: string;
};

type SendNotificationProps = {
  openDialog: boolean;
  handleOpenDialog: () => void;
  handleCloseDialog: () => void;
  date: Date;
  notifyStaffMembers: boolean;
} & MenuItemProps;

const SendNotification = forwardRef<HTMLLIElement, SendNotificationProps>(
  ({ openDialog, handleOpenDialog, handleCloseDialog, date, notifyStaffMembers, ...menuItemProps }, ref) => {
    const { data } = useSubscription(listAssignedStaffMembersSubscription, {
      variables: { date: format(date, 'YYYY-MM-DD') },
      skip: !openDialog,
    });

    const recipients = useMemo(
      () => (data?.listAssignedStaffMembers || []).map((e: StaffMember) => ({ ...e, id: e.staffId })),
      [data]
    );

    const [doSend] = useMutation(sendNewScheduleNotification);

    const scope = useScope();

    const sendNotifications = async (recipients: Contact[], message: string) => {
      const staffContacts: StaffMemberContact[] = recipients.map(e => ({
        staffId: e.id,
        staffHospitalId: scope?.hospital?.id,
        phoneNumber: e.phoneNumber,
      }));

      await doSend({
        variables: {
          staffContacts,
          message,
          date: format(date, 'YYYY-MM-DD'),
        },
      });
    };

    const { button, ...restMenuItemProps } = menuItemProps;

    return (
      <>
        <TooltipWrapper title={'No staff to notify'} isDisabled={!notifyStaffMembers}>
          <MenuItem {...restMenuItemProps} ref={ref} disabled={!notifyStaffMembers} onClick={handleOpenDialog}>
            Staff Members
          </MenuItem>
        </TooltipWrapper>
        {openDialog && (
          <SendNotificationDialog
            date={date}
            open={openDialog}
            recipients={recipients}
            sendNotifications={sendNotifications}
            destination={Destination.StaffMember}
            handleClose={handleCloseDialog}
          />
        )}
      </>
    );
  }
);

SendNotification.displayName = 'SendNotificationStaffMember';

export default SendNotification;
